import { filter, includes, map } from "lodash";
import {
  getActiveGroupTypes,
  getActiveDemandGroupTypes,
  getActiveSupplyGroupTypes,
} from "src/util/group";

/**
 * Helper function to resolve group type filter options to actual group type IDs
 * This is specifically for tag type configuration and should not be used elsewhere
 */
export const resolveGroupTypeFilter = (selectedGroupTypes, groupTypes) => {
  if (!selectedGroupTypes || !Array.isArray(selectedGroupTypes)) {
    return [];
  }

  const resolvedTypes = [];

  for (const selectedType of selectedGroupTypes) {
    if (selectedType === "ALL") {
      // Include all active group types
      const activeGroupTypes = getActiveGroupTypes(groupTypes);
      resolvedTypes.push(...map(activeGroupTypes, "id"));
    } else if (selectedType === "ALL_DEMAND") {
      // Include all active demand group types
      const demandGroupTypes = getActiveDemandGroupTypes(groupTypes);
      resolvedTypes.push(...map(demandGroupTypes, "id"));
    } else if (selectedType === "ALL_SUPPLY") {
      // Include all active supply group types
      const supplyGroupTypes = getActiveSupplyGroupTypes(groupTypes);
      resolvedTypes.push(...map(supplyGroupTypes, "id"));
    } else if (selectedType === "ALL_DOES_NOT_PARTICIPATE") {
      // Include all group types that are NOT supply or demand
      const allGroupTypes = getActiveGroupTypes(groupTypes);
      const supplyGroupIds = map(getActiveSupplyGroupTypes(groupTypes), "id");
      const demandGroupIds = map(getActiveDemandGroupTypes(groupTypes), "id");
      resolvedTypes.push(
        ...map(
          filter(allGroupTypes, (groupType) => {
            return (
              !includes(supplyGroupIds, groupType.id) &&
              !includes(demandGroupIds, groupType.id)
            );
          }),
          "id"
        )
      );
    } else {
      // Individual group type
      resolvedTypes.push(selectedType);
    }
  }

  return resolvedTypes;
};

/**
 * Get the type description for a group type (root, demand, supply, does not participate)
 * This is specifically for tag type configuration display
 * @param {Object} groupType - The group type object
 * @returns {string} The type description
 */
export const getGroupTypeDescription = (groupType) => {
  if (!groupType) {
    return "Does not participate";
  }

  if (groupType.isRoot) {
    return "Root";
  }

  if (groupType.isDemand) {
    return "Demand";
  }

  if (groupType.isSupply) {
    return "Supply";
  }

  return "Does not participate";
};

/**
 * Get the full name with type description for a group type
 * This is specifically for tag type configuration display
 * @param {Object} groupType - The group type object
 * @returns {string} The full name with type description
 */
export const getGroupTypeFullName = (groupType) => {
  if (!groupType) {
    return "Unknown";
  }

  const typeDescription = getGroupTypeDescription(groupType);
  return `${groupType.name} (${typeDescription})`;
};
